import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    ElementRef,
    forwardRef,
    inject,
    input,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    DocumentItemPartial,
    DocumentItemTypeEnum,
    FavoriteFieldEntity,
    UserController,
} from '@domain';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import { CardTypeFormatPipe, FileSizePartsPipe } from '@shared/pipes';
import { RaCountUpComponent } from 'ra-ui';
import { DocumentFieldsController } from 'src/app/domain/controllers/document/document-fields.controller';
import { TrashDocumentItemEntity } from 'src/app/domain/entities/trash-document-item.entity';
import { RaSquareIconComponent } from 'src/app/shared/components/ra-square-icon/ra-square-icon.component';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { RaClickOutsideDirective } from 'src/app/shared/directives';
import { RaEditTooltipDirective } from '../../../directives/ra-edit-tooltip/ra-edit-tooltip.directive';
import { EditDataService } from '../../../services/_edit-data.service';
import { EditItemsService } from '../../../services/edit-items.service';
import { EditService } from '../../../services/edit-service';
import { OriginalTextService } from '../../../services/original-text.service';
import { TextEditingService } from '../../../services/text-editing.service';
import { ModalCreateFavoriteFieldComponent } from '../../modals/modal-create-favorite-field/modal-create-favorite-field.component';
import { IaOptionsComponent } from './components/ia-options/ia-options.component';
import { CardTypes } from './enums/card-types.enum';
import { DynamicActionModel } from './model/dynamic-action.model';
import { RailPopoverService } from './rail-popover.service';
import { RAIL_MENU_HOST } from './rail-menu-host';
import { RaRailFitDirective } from './rail-fit.directive';
import { RaRailMenuDirective, watchMenuDismiss } from './rail-menu.directive';
import { SpacingChipComponent } from './spacing-chip/spacing-chip.component';
import { itemSpacingOf, spacingRem } from '../../../../../item-spacing';

@Component({
    selector: 'card-base',
    templateUrl: 'card-base.component.html',
    styleUrl: 'card-base.component.scss',
    imports: [
        RaRailFitDirective,
        CommonModule,
        DragDropModule,
        FormsModule,
        FileSizePartsPipe,
        RaCountUpComponent,
        RaSquareIconComponent,
        RaIconComponent,
        IaOptionsComponent,
        RaClickOutsideDirective,
        CardTypeFormatPipe,
        RaEditTooltipDirective,
        ModalCreateFavoriteFieldComponent,
        RaRailMenuDirective,
        SpacingChipComponent,
        TranslatePipe,
    ],
    providers: [
        CardTypeFormatPipe,
        { provide: RAIL_MENU_HOST, useExisting: forwardRef(() => CardBaseComponent) },
    ],
})
export class CardBaseComponent implements OnInit {
    // * [Inputs]
    public field = input<DocumentItemPartial>();
    public dynamicActions = input<DynamicActionModel[]>([]);
    public favoriteField = input<FavoriteFieldEntity>();
    public trashField = input<TrashDocumentItemEntity>();
    public type = input<CardTypes>(CardTypes.DEFAULT);
    public selected = input<boolean>(false);
    public disabled = input<boolean>(false);
    /** Varinha da IA no rail. A grade de indicadores não tem texto corrido para a IA. */
    public showAi = input<boolean>(true);
    /**
     * Campo com texto corrido: libera o "Usar IA" no menu ⋯. Falso nos blocos sem texto
     * (indicadores, semáforo).
     */
    public textZone = input<boolean>(true);
    /** Nome do grupo das ações do próprio campo no menu ⋯ (ex.: "Foto"). */
    public overflowTitle = input<string>('');

    // * [Variables]
    public typeIconDefault = signal('text');
    public showIaOptions = signal(false);
    public showMoreMenu = signal(false);
    public showModalCreateFavoriteField = signal(false);
    /** Popover projetado no slot card-toolbar aberto (ex.: menu de tipo do
     *  text-card) — mantém o card acima do composer enquanto durar. */
    public externalPopoverOpen = signal(false);
    /** Menus `raRailMenu` abertos dentro deste card (ver RAIL_MENU_HOST). */
    public railMenusOpen = signal(0);
    /** O painel de formato da mídia está com este card (ver RAIL_MENU_HOST). */
    public lookPanelOpen = signal(false);
    /**
     * O card específico tem um menu do rail aberto (cor, estilo, colunas...). Mantém o
     * rail visível enquanto o mouse vai até o menu, que hoje pode abrir para fora do card,
     * e sobe o card acima do composer.
     */
    public popoverOpen = input<boolean>(false);
    /**
     * Card de BLOCO (destaque, indicadores, tabela, semáforo): tem fundo próprio, então
     * pede mais respiro que um parágrafo para não encostar no vizinho.
     */
    public block = input<boolean>(false);
    public cardTypes = CardTypes;


    // * [Injects]
    public editService = inject(EditService);
    public userController = inject(UserController);
    public editItemsService = inject(EditItemsService);
    private _raToastService = inject(RaToastService);
    private _editDataService = inject(EditDataService);
    private _documentFieldsController = inject(DocumentFieldsController);
    private _translate = inject(TranslateService);
    private _textEditing = inject(TextEditingService);
    private _host = inject<ElementRef<HTMLElement>>(ElementRef);
    private _originalTextService = inject(OriginalTextService);
    private _railPopovers = inject(RailPopoverService);
    private _iaOptions = viewChild(IaOptionsComponent, { read: ElementRef });

    /** True quando o editor inline deste card está em foco. */
    public isEditing = computed(
        () => this._textEditing.editingFieldId() === this.getCurrentField()?._id
    );

    constructor() {
        // O menu da IA não é um `raRailMenu` (fica montado e só se esconde): entra na
        // mesma fila, para abrir a IA fechar o menu aberto em outro card e vice-versa.
        // Como os outros menus do rail, fecha quando o usuário rola a folha ou aperta Esc.
        effect(onCleanup => {
            if (!this.showIaOptions()) return;
            const close = (): void => this.showIaOptions.set(false);
            const release = this._railPopovers.open(close);
            const menu = this._iaOptions()?.nativeElement;
            const unwatch = menu ? watchMenuDismiss(menu, close) : null;
            onCleanup(() => {
                release();
                unwatch?.();
            });
        });
    }

    ngOnInit(): void {
        this._init();
        this.setLabelFromType();
    }

    /**
     * Popovers do rail são exclusivos: abrir um fecha o outro.
     * (O menu de tipo do text-card se coordena com estes via viewChild.)
     */
    public toggleIaOptions(): void {
        this.showMoreMenu.set(false);
        this.showIaOptions.set(!this.showIaOptions());
    }

    public toggleMoreMenu(): void {
        this.showIaOptions.set(false);
        this.showMoreMenu.set(!this.showMoreMenu());
    }

    /** Item "Usar IA" do menu ⋯: fecha o menu e abre as opções da IA. */
    public openIaFromMenu(): void {
        this.showMoreMenu.set(false);
        this.showIaOptions.set(true);
    }

    /** Item "Espaçamento" do menu ⋯: abre o menu do chip, ancorado no ⋯. */
    public openSpacingFromMenu(): void {
        this.showMoreMenu.set(false);
        this._spacingChip()?.showMenu.set(true);
    }

    /**
     * Clicar em qualquer ponto do card o torna o alvo da barra de formatação (tamanho,
     * cor, tipo). O editor inline faz o mesmo ao ganhar foco; aqui cobre foto, tabela e os
     * outros cards sem editor de texto.
     */
    public onPointerDown(): void {
        if (this.type() !== CardTypes.DEFAULT) return;
        this._textEditing.setActive(this.getCurrentField()?._id ?? null);
    }

    /**
     * Verifica se os dados do tipo são válidos
     */
    private _init(): void {
        switch (this.type()) {
            case 'default':
                if (!this.field()) throw new Error('Field is required');
                break;
            case 'trash':
                if (!this.trashField()) throw new Error('Trash field is required');
                break;
            case 'favorite':
                if (!this.favoriteField()) throw new Error('Favorite field is required');
                break;
        }
    }

    /**
     * Seta o label do tipo do campo
     * @param field Campo
     */
    private setLabelFromType(): void {
        const field = this.getCurrentField();

        switch (field.type) {
            case DocumentItemTypeEnum.TEXT:
                this.typeIconDefault.set('text');
                break;
            case DocumentItemTypeEnum.TITLE:
                this.typeIconDefault.set('text');
                break;
            case DocumentItemTypeEnum.SUBTITLE:
                this.typeIconDefault.set('smallcaps');
                break;
            case DocumentItemTypeEnum.IMAGE:
                this.typeIconDefault.set('gallery');
                break;
            case DocumentItemTypeEnum.GROUP_IMAGE:
                this.typeIconDefault.set('grid-2');
                break;
            case DocumentItemTypeEnum.VIDEO:
                this.typeIconDefault.set('video-play');
                break;
            case DocumentItemTypeEnum.GROUP_VIDEO:
                this.typeIconDefault.set('video-play');
                break;
            case DocumentItemTypeEnum.CALLOUT:
                this.typeIconDefault.set('callout');
                break;
            case DocumentItemTypeEnum.STATS:
                this.typeIconDefault.set('stats');
                break;
            case DocumentItemTypeEnum.TABLE:
                this.typeIconDefault.set('table');
                break;
            case DocumentItemTypeEnum.SEMAPHORE:
                this.typeIconDefault.set('semaphore');
                break;
            case DocumentItemTypeEnum.CHART:
                this.typeIconDefault.set('chart');
                break;
        }
    }

    // Realiza a correção do texto
    public fixText(): void {
        const field = this.getCurrentField();

        // Obtém o texto a ser corrigido
        let textValue;
        if (
            field.type === DocumentItemTypeEnum.TEXT ||
            field.type === DocumentItemTypeEnum.TITLE ||
            field.type === DocumentItemTypeEnum.SUBTITLE
        ) {
            textValue = field.value;
        } else if (
            field.type === DocumentItemTypeEnum.IMAGE ||
            field.type === DocumentItemTypeEnum.VIDEO ||
            field.type === DocumentItemTypeEnum.GROUP_IMAGE ||
            field.type === DocumentItemTypeEnum.GROUP_VIDEO ||
            // O texto da caixa de destaque mora na descrição, como nas mídias.
            field.type === DocumentItemTypeEnum.CALLOUT
        ) {
            textValue = field.description;
        }

        // Verifica se o texto é válido
        if (!textValue || textValue.length === 0) {
            this._raToastService.warning({
                message: this._translate.instant('document.edit.cardBase.noTextToCorrect'),
            });
            return;
        }

        // Inicia o loading
        this.startLoading('document.edit.cardBase.loadingSpellCheck');

        const documentId = this._editDataService.getDocumentId();
        const sessionId = this._editDataService.getCurrentSessionId();
        const fieldId = field._id;

        // Chama a IA de correção ortográfica
        this._documentFieldsController
            .textCorrectionIa(documentId, sessionId, fieldId, textValue)
            .subscribe({
                next: () => {
                    this.stopLoading();
                    this._afterIaTextChange(
                        documentId,
                        fieldId,
                        textValue,
                        'domain.document.success.spellcheckDone'
                    );
                },
                error: () => this.stopLoading(),
            });
    }

    // Realiza a correção ortográfica usando como prompt
    public useAsPrompt(): void {
        const field = this.getCurrentField();

        // Obtém o texto a ser corrigido
        let textValue;
        if (
            field.type === DocumentItemTypeEnum.TEXT ||
            field.type === DocumentItemTypeEnum.TITLE ||
            field.type === DocumentItemTypeEnum.SUBTITLE
        ) {
            textValue = field.value;
        } else if (
            field.type === DocumentItemTypeEnum.IMAGE ||
            field.type === DocumentItemTypeEnum.VIDEO ||
            field.type === DocumentItemTypeEnum.GROUP_IMAGE ||
            field.type === DocumentItemTypeEnum.GROUP_VIDEO ||
            // O texto da caixa de destaque mora na descrição, como nas mídias.
            field.type === DocumentItemTypeEnum.CALLOUT
        ) {
            textValue = field.description;
        }

        // Verifica se o texto é válido
        if (!textValue || textValue.length === 0) {
            this._raToastService.warning({
                message: this._translate.instant('document.edit.cardBase.noTextForPrompt'),
            });
            return;
        }

        // Inicia o loading
        this.startLoading('document.edit.cardBase.loadingAiPromptResponse');

        const documentId = this._editDataService.getDocumentId();
        const sessionId = this._editDataService.getCurrentSessionId();
        const fieldId = field._id;

        // Chama a IA de uso como prompt
        this._documentFieldsController
            .textPromptIA(documentId, sessionId, fieldId, textValue)
            .subscribe({
                next: () => {
                    this.stopLoading();
                    this._afterIaTextChange(
                        documentId,
                        fieldId,
                        textValue,
                        'domain.document.success.spellcheckDone'
                    );
                },
                error: () => this.stopLoading(),
            });
    }

    // Realiza a descrição automática
    public generateAutomaticDescription(): void {
        const field = this.getCurrentField();

        // Vídeo demora mais (o servidor baixa, extrai quadros e transcreve a fala), então o
        // overlay avisa o que está acontecendo e o toast de sucesso fala de vídeo.
        const isVideo =
            field?.type === DocumentItemTypeEnum.VIDEO ||
            field?.type === DocumentItemTypeEnum.GROUP_VIDEO;
        this.startLoading(
            isVideo
                ? 'document.edit.cardBase.loadingVideoDescription'
                : 'document.edit.cardBase.loadingAutoDescription'
        );

        const documentId = this._editDataService.getDocumentId();
        const sessionId = this._editDataService.getCurrentSessionId();
        const fieldId = field._id;
        const description = field?.description;

        // Chama a IA de descrição automática
        this._documentFieldsController
            .imagesDescriptionIa(documentId, sessionId, fieldId, description)
            .subscribe({
                next: () => {
                    this.stopLoading();
                    this._afterIaTextChange(
                        documentId,
                        fieldId,
                        description,
                        isVideo
                            ? 'domain.document.success.videoDescDone'
                            : 'domain.document.success.imageDescDone'
                    );
                },
                error: () => this.stopLoading(),
            });
    }

    /**
     * Pós-IA: guarda o texto que o usuário tinha antes (primeiro backup vence) e
     * mostra o toast de sucesso com "Desfazer" (os endpoints de IA não emitem
     * mais o toast — só aqui ele pode carregar a ação).
     */
    private _afterIaTextChange(
        documentId: string,
        fieldId: string,
        previousText: string | undefined,
        successKey: string
    ): void {
        if (previousText) this._originalTextService.save(documentId, fieldId, previousText);

        this.editItemsService.showIaSuccessToast(fieldId, successKey);
    }

    /** O campo tem texto pré-IA guardado para restaurar? */
    public hasOriginalBackup(): boolean {
        return this.editItemsService.hasOriginalText(this.getCurrentField()?._id);
    }

    /** Restaura o texto que o campo tinha antes da IA sobrescrever. */
    public restoreOriginalText(): void {
        const fieldId = this.getCurrentField()?._id;
        if (fieldId) this.editItemsService.restoreOriginalText(fieldId);
    }

    /** O chip "Espaçamento" deste card (sem botão: só o menu, dentro do ⋯). */
    private _spacingChip = viewChild(SpacingChipComponent);

    /**
     * Espaçamento próprio do campo (configs spaceTop/Bottom/Left/Right) como margem do card,
     * pelas variáveis --card-sp-* do SCSS: o rail e o gutter descontam os lados para
     * continuarem presos à coluna, e não ao card que encolheu. Ao vivo enquanto o menu
     * "Espaçamento" mexe. Lixeira e favoritos ficam sem (template).
     */
    public readonly spacing = computed(() =>
        spacingRem(
            this._spacingChip()?.shown() ??
                itemSpacingOf(this.getCurrentField()?.configs as Record<string, unknown>)
        )
    );

    /** Este campo começa em folha nova (atributo do item; ver DocumentConfigPartial). */
    public readonly startsOnNewPage = computed(
        () => this.getCurrentField()?.configs?.pageBreakBefore === true
    );

    /**
     * A opção não aparece na quebra legada (item PAGE_BREAK, que o modelo novo substitui)
     * nem dentro de pasta de checklist, onde a quebra não teria como valer.
     */
    public readonly canStartOnNewPage = computed(() => {
        const field = this.getCurrentField();
        if (!field || field.type === DocumentItemTypeEnum.PAGE_BREAK) return false;

        return (field.parentFolderId ?? null) === null;
    });

    /**
     * Liga/desliga o começo em folha nova. É só um atributo do campo: nada é inserido na
     * lista, então reordenar os itens não perde nem duplica a quebra.
     */
    public toggleStartOnNewPage(): void {
        const field = this.getCurrentField();
        if (!field) return;

        this.editItemsService.updateItemConfig(field._id, {
            ...(field.configs ?? {}),
            pageBreakBefore: !this.startsOnNewPage(),
        });
    }

    /** Tipos que o "Duplicar campo" copia (os de conteúdo; espelho da API). */
    private static readonly DUPLICABLE_TYPES = new Set<string>([
        DocumentItemTypeEnum.TEXT,
        DocumentItemTypeEnum.TITLE,
        DocumentItemTypeEnum.SUBTITLE,
        DocumentItemTypeEnum.IMAGE,
        DocumentItemTypeEnum.GROUP_IMAGE,
        DocumentItemTypeEnum.VIDEO,
        DocumentItemTypeEnum.GROUP_VIDEO,
        DocumentItemTypeEnum.CALLOUT,
        DocumentItemTypeEnum.STATS,
        DocumentItemTypeEnum.TABLE,
        DocumentItemTypeEnum.SEMAPHORE,
        DocumentItemTypeEnum.CHART,
    ]);

    public canDuplicate(): boolean {
        const type = this.getCurrentField()?.type;
        return !!type && CardBaseComponent.DUPLICABLE_TYPES.has(type);
    }

    public duplicateField(): void {
        const field = this.getCurrentField();
        if (field?._id) this.editItemsService.duplicateField(field._id);
    }

    /**
     * As ações de IA do card usam o overlay full-screen bloqueante (mesmo padrão de
     * "corrigir todos os textos") em vez do loading pequeno ao lado do card.
     * @param messageKey Chave i18n da mensagem exibida no overlay
     */
    private startLoading(messageKey: string): void {
        this.editService.fieldIaActionKey.set(messageKey);
    }

    private stopLoading(): void {
        this.editService.fieldIaActionKey.set(null);
    }

    /**
     * "Encontrar no preview": abre o painel do PDF e manda a folha rolar até este item.
     *
     * Vale para todo tipo de card, inclusive os que NÃO saem no impresso (quebra de página,
     * vídeo com `showVideo` desligado). Nesses, o iframe responde que não achou e o painel
     * avisa — melhor que um clique que não faz nada.
     */
    public locateInPreview(): void {
        const field = this.getCurrentField();
        if (!field?._id) return;

        this._editDataService.openPdfPreview();
        this._editDataService.requestPreviewScroll(field._id);
    }

    /**
     * Retorna o campo atual
     * @returns DocumentItemPartial | TrashDocumentItemEntity | FavoriteFieldEntity
     */
    public getCurrentField(): DocumentItemPartial {
        switch (this.type()) {
            case 'default':
                return this.field();
            case 'trash':
                return this.trashField().item;
            case 'favorite':
                return this.favoriteField().field;
        }
    }

    /**
     * Seleciona o item
     * Usado para selecionar itens para mover ou remover
     * @param event Evento
     */
    public selectItem(event: Event): void {
        event.stopPropagation();

        const field = this.getCurrentField();
        const target = event.target as HTMLInputElement | null;
        const isSelected = target?.checked ?? !field.selected;
        field.selected = isSelected;

        // Adiciona ou remove o item dos itens selecionados
        if (field.selected) {
            this._editDataService.addSelectedItem(field);
        } else {
            this._editDataService.removeSelectedItem(field);
        }
    }
}
